import type {OpenCopilotChatEvent} from '@github-ui/copilot-chat-events'
import {sendEvent} from '@github-ui/hydro-analytics'

/**
 * Derive the `source` tag for the `copilot.open_copilot_chat` telemetry event
 * from an incoming open event's payload. Mirrors the v1 emitter in
 * `CopilotChatManager.handleOpenPanelEvent` so both UIs surface identical
 * `source` values in the shared event stream.
 */
export function deriveOpenCopilotChatSource(event: OpenCopilotChatEvent): string {
  const payload = event.payload
  // Some callers dispatch a bare `Event` or `CustomEvent('open-copilot-chat')`
  // with no structured payload; guard so the emit path doesn't throw and
  // suppress downstream open handling.
  if (!payload) return 'blob unknown'
  return payload.id ? `element ${payload.id}` : `blob ${payload.intent}`
}

/**
 * Emit the v2 `copilot.open_copilot_chat` telemetry event. Call this at the
 * point where a v2 UI (ChatAppEmbedded) takes ownership of an open event, so
 * dashboards can differentiate v1 vs. v2 opens using a single event stream.
 * The v1 emitter lives in `CopilotChatManager.handleOpenPanelEvent`.
 */
export function emitOpenCopilotChatV2(event: OpenCopilotChatEvent): void {
  sendEvent('copilot.open_copilot_chat', {
    source: deriveOpenCopilotChatSource(event),
    chat_version: 'v2',
  })
}
